import { HStack } from '@chakra-ui/react';
import {
  ChatBubbleLeftRightIcon,
  CheckIcon,
  PaperClipIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { ButtonGroup } from '@main/complykit/components/button-group';
import { IconButton } from '@main/complykit/components/icon-button';
import { Status } from '@main/complykit/components/status';
import { Text } from '@main/complykit/components/text';
import { Vendor_Questionnaire_Form_Answer_Statuses_Enum } from '@main/graphql/types.generated';
import { formatDate } from '@main/shared/utils';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useQuestionnnaireFormContext } from './context';
import {
  QuestionnaireFormFieldConfig,
  QuestionnnaireFieldWrapperProps,
  QuestionnnaireFormFieldAnswer,
  QuestionnnaireFormMode,
} from './form-types';
import { QUESTION_STATUSES } from './utils';

export interface QuestionnaireFieldHeaderProps extends QuestionnnaireFieldWrapperProps {
  config: QuestionnaireFormFieldConfig;
  answer: QuestionnnaireFormFieldAnswer;
  setShowComment: Dispatch<SetStateAction<boolean>>;
  setShowFiles: Dispatch<SetStateAction<boolean>>;
}

export function QuestionnaireFieldHeader(props: QuestionnaireFieldHeaderProps) {
  const { t } = useTranslation();
  const { trigger } = useFormContext();
  const { questionFields } = useQuestionnnaireFormContext();

  const { config, answer, form } = props;
  const isAnswerMode = form.mode === QuestionnnaireFormMode.Answer && !form.isSubmitted;
  const isCompleted = answer.status === Vendor_Questionnaire_Form_Answer_Statuses_Enum.Completed;
  const totalQuestions = questionFields.length;
  const questionIdx = useMemo(
    () => questionFields.findIndex((field) => field.name === config.name) + 1,
    [config.name, questionFields],
  );

  const setCompletedAnswer = async (isCompleted: boolean) => {
    const isValid = await trigger(config.name);

    if (!isValid && isCompleted) {
      return;
    }

    await props.onAnswerChange?.(answer, {
      status: isCompleted
        ? Vendor_Questionnaire_Form_Answer_Statuses_Enum.Completed
        : Vendor_Questionnaire_Form_Answer_Statuses_Enum.Pending,
    });
  };

  return (
    <HStack alignItems="start" justifyContent="space-between" spacing={4}>
      <HStack alignItems="start">
        <Text weight="semibold" size="xs">
          {t('questionnaires.form.questionNo', { number: questionIdx, total: totalQuestions })}
        </Text>
        <Status tone={QUESTION_STATUSES[props.answer.status].tone}>
          {t(QUESTION_STATUSES[props.answer.status].value)}
        </Status>
      </HStack>
      {isAnswerMode &&
        (isCompleted ? (
          <IconButton
            variant="outline"
            icon={PencilIcon}
            label={t('questionnaires.form.edit')}
            onPress={() => void setCompletedAnswer(false)}
          />
        ) : (
          <HStack spacing={3}>
            {props.answer.updated_at && (
              <Text size="xs" color="muted">
                {t('questionnaires.form.savedOn', { date: formatDate(props.answer.updated_at) })}
              </Text>
            )}
            <ButtonGroup label={t('questionnaires.form.answerActions')}>
              <IconButton
                icon={PaperClipIcon}
                label={t('questionnaires.form.addSupportingFiles')}
                onPress={() => props.setShowFiles((isShow) => !isShow)}
              />
              <IconButton
                icon={ChatBubbleLeftRightIcon}
                label={t('questionnaires.form.addComment')}
                onPress={() => props.setShowComment((isShow) => !isShow)}
              />
            </ButtonGroup>
            <IconButton
              variant="solid"
              icon={CheckIcon}
              label={t('questionnaires.form.complete')}
              onPress={() => void setCompletedAnswer(true)}
            />
          </HStack>
        ))}
    </HStack>
  );
}
